import moment from 'moment';
import { capitalizeWord } from '@/filters/capitalize';
import { competenceLabel, competenceShortLabel } from '@/app/billing/batch/service';

export const GUIDES_PATH = '/financial/billing-accounts';
export const BATCHES_PATH = '/financial/billing-batches';
export const ATTENDANCES_PATH = '/financial/appointment-attendances';
export const PRODUCTIVE_STATUSES = [
  'scheduled', 'confirmed', 'waiting', 'screening', 'in_attendance',
  'finished', 'report', 'payment', 'pending',
].join(',');
export const GLOSSES_PATH = '/financial/billing-glosses';
export const SESSIONS_PATH = '/financial/cash-register-sessions';
export const TRANSACTIONS_PATH = '/financial/transactions';

export const chartColors = {
  billed: '#4737FF',
  billedFill: 'rgba(71, 55, 255, .55)',
  received: '#15CE9A',
  receivedFill: 'rgba(21, 206, 154, .55)',
  glossed: '#FF6B6B',
  glossedFill: 'rgba(255, 107, 107, .15)',
};

export function num(value) {
  return Number(value) || 0;
}

export function list(value) {
  return Array.isArray(value) ? value : [];
}

export function idOf(value) {
  if (!value) return null;
  return String(value);
}

export function percentOf(part, total) {
  const base = num(total);
  if (base <= 0) return 0;
  return (num(part) / base) * 100;
}

export function formatPercent(value, decimals = 1) {
  return `${num(value).toFixed(decimals).replace('.', ',')}%`;
}

export function formatDate(value) {
  if (!value) return null;
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format('L') : null;
}

export function currentCompetence() {
  return moment().format('YYYY-MM');
}

export function competenceOf(value) {
  const parsed = moment(value, 'YYYY-MM', true);
  return parsed.isValid() ? parsed.format('YYYY-MM') : currentCompetence();
}

export function competenceRange(value) {
  const base = moment(competenceOf(value), 'YYYY-MM');
  return {
    startDate: base.clone().startOf('month').format('YYYY-MM-DD'),
    endDate: base.clone().endOf('month').format('YYYY-MM-DD'),
  };
}

export function competenceName(value) {
  return competenceLabel(competenceOf(value));
}

export function competenceTitle(value) {
  return capitalizeWord(competenceName(value));
}

export function competenceMonth(value) {
  if (!moment(value, 'YYYY-MM', true).isValid()) return String(value || '');
  return competenceShortLabel(value).replace(/\/\d{2}(\d{2})$/, '/$1');
}

export function daysSince(value) {
  if (!value) return null;
  const parsed = moment(value);
  if (!parsed.isValid()) return null;
  return moment().startOf('day').diff(parsed.startOf('day'), 'days');
}

export function daysUntil(value) {
  const days = daysSince(value);
  return days === null ? null : -days;
}

export function pluralize(count, singular, plural) {
  return `${count} ${count === 1 ? singular : plural}`;
}

function isBlank(value) {
  return value === undefined || value === null || value === '';
}

function items(value) {
  return list(value).filter(item => item && typeof item === 'object');
}

function valueBlock(source) {
  const block = source || {};
  return { value: num(block.value), count: num(block.count) };
}

function countBlock(source) {
  const block = source || {};
  return { count: num(block.count), value: num(block.value) };
}

function overviewOf(source) {
  const overview = source || {};
  const billed = overview.billed || {};
  const glossed = overview.glossed || {};
  const expected = overview.expected || {};
  const billedValue = num(billed.value);
  const glossedValue = num(glossed.value);
  return {
    production: valueBlock(overview.production),
    billed: { value: billedValue, batches: num(billed.batches) },
    waiting: valueBlock(overview.waiting),
    glossed: {
      value: glossedValue,
      count: num(glossed.count),
      percent: isBlank(glossed.percent)
        ? percentOf(glossedValue, billedValue)
        : num(glossed.percent),
    },
    expected: {
      value: num(expected.value),
      overdueValue: num(expected.overdueValue),
      overdueCount: num(expected.overdueCount),
    },
  };
}

function particularOf(source) {
  const particular = source || {};
  const overdue = particular.overdue || {};
  const month = num(particular.month);
  const count = num(particular.count);
  let ticket = num(particular.ticket);
  if (isBlank(particular.ticket) && count > 0) {
    ticket = month / count;
  }
  return {
    today: num(particular.today),
    month,
    count,
    ticket,
    overdue: {
      value: num(overdue.value),
      count: num(overdue.count),
      patients: num(overdue.patients),
    },
  };
}

export function normalize(payload) {
  const data = payload && typeof payload === 'object' ? payload : {};
  const period = data.period || {};
  const guides = data.guides || {};
  return {
    period: {
      startDate: period.startDate || null,
      endDate: period.endDate || null,
    },
    overview: overviewOf(data.overview),
    guides: {
      pending: countBlock(guides.pending),
      expired: countBlock(guides.expired),
      glossed: countBlock(guides.glossed),
    },
    batchesByInsurance: items(data.batchesByInsurance).map(item => ({
      insuranceId: idOf(item.insuranceId),
      insuranceName: item.insuranceName || 'Convênio não identificado',
      planName: item.planName || '',
      batches: num(item.batches),
      batchId: item.batchId || null,
      guides: num(item.guides),
      informed: num(item.informed),
      glossed: num(item.glossed),
      expectedPaymentAt: item.expectedPaymentAt || null,
      oldestSentAt: item.oldestSentAt || null,
    })),
    glossesByReason: items(data.glossesByReason).map(item => ({
      code: item.code || '',
      description: item.description || 'Motivo não informado',
      count: num(item.count),
      value: num(item.value),
      percent: num(item.percent),
    })),
    glossesByInsurance: items(data.glossesByInsurance).map(item => ({
      insuranceId: idOf(item.insuranceId),
      insuranceName: item.insuranceName || 'Convênio não identificado',
      informed: num(item.informed),
      glossed: num(item.glossed),
      percent: isBlank(item.percent)
        ? percentOf(item.glossed, item.informed)
        : num(item.percent),
    })),
    particular: particularOf(data.particular),
    monthly: items(data.monthly).map(item => ({
      competence: item.competence || '',
      informed: num(item.informed),
      glossed: num(item.glossed),
      received: num(item.received),
    })),
  };
}

export function blankData() {
  return normalize(null);
}

export function guidesRoute(situation, filter) {
  const { startDate, endDate } = competenceRange(filter.competence);
  if (situation === 'pending') {
    const query = {
      startDate,
      endDate,
      hasAccount: 'false',
      statuses: PRODUCTIVE_STATUSES,
      planType: 'insurance',
      excludeLegacy: 'true',
    };
    if (filter.insurancePlanId && filter.insurancePlanId !== 'ALL') {
      query.insurancePlanId = filter.insurancePlanId;
    }
    return { path: ATTENDANCES_PATH, query };
  }
  const query = { startDate, endDate };
  if (situation) query.situation = situation;
  if (filter.insurancePlanId) query.insurancePlanId = filter.insurancePlanId;
  return { path: GUIDES_PATH, query };
}

export function glossReasonRoute(row, filter) {
  const { startDate, endDate } = competenceRange(filter.competence);
  const query = { startDate, endDate, code: row.code };
  if (row.description) query.codeDescription = row.description;
  if (filter.insurancePlanId && filter.insurancePlanId !== 'ALL') {
    query.planId = filter.insurancePlanId;
  }
  return { path: GLOSSES_PATH, query };
}

export function glossInsuranceRoute(row, filter) {
  const { startDate, endDate } = competenceRange(filter.competence);
  const query = { startDate, endDate };
  if (row.insuranceId) query.insuranceId = row.insuranceId;
  if (filter.insurancePlanId && filter.insurancePlanId !== 'ALL') {
    query.planId = filter.insurancePlanId;
  }
  return { path: GLOSSES_PATH, query };
}

export function productionRoute(filter) {
  const { startDate, endDate } = competenceRange(filter.competence);
  const query = {
    startDate,
    endDate,
    statuses: PRODUCTIVE_STATUSES,
    planType: 'insurance',
    excludeLegacy: 'true',
  };
  if (filter.insurancePlanId && filter.insurancePlanId !== 'ALL') {
    query.insurancePlanId = filter.insurancePlanId;
  }
  return { path: ATTENDANCES_PATH, query };
}

export function billedRoute(filter) {
  if (filter.insurancePlanId && filter.insurancePlanId !== 'ALL') {
    return {
      path: BATCHES_PATH,
      query: { competence: competenceOf(filter.competence), planId: filter.insurancePlanId },
    };
  }
  const { startDate, endDate } = competenceRange(filter.competence);
  return { path: GUIDES_PATH, query: { startDate, endDate, link: 'with' } };
}

export function glossesRoute(filter) {
  return glossInsuranceRoute({}, filter);
}

export function expectedRoute(filter) {
  const { startDate, endDate } = competenceRange(filter.competence);
  return {
    path: TRANSACTIONS_PATH,
    query: {
      type: 'revenue',
      status: 'open',
      dateType: 'issueDate',
      onlyDebt: 'batch',
      startDate,
      endDate,
    },
  };
}

export function cashRoute(filter, scope) {
  const { startDate, endDate } = competenceRange(filter.competence);
  const day = moment().format('YYYY-MM-DD');
  const period = scope === 'today'
    ? { startDate: day, endDate: day }
    : { startDate, endDate };
  return { path: SESSIONS_PATH, query: period };
}

export function overdueRoute(filter) {
  const { startDate, endDate } = competenceRange(filter.competence);
  const yesterday = moment().subtract(1, 'day').format('YYYY-MM-DD');
  return {
    path: TRANSACTIONS_PATH,
    query: {
      type: 'revenue',
      status: 'open',
      dateType: 'dueDate',
      onlyDebt: 'cashRegister',
      startDate,
      endDate: endDate < yesterday ? endDate : yesterday,
    },
  };
}

export function batchesRoute(row, filter) {
  if (row && row.batchId) return { path: `${BATCHES_PATH}/${row.batchId}` };
  const query = { competence: competenceOf(filter.competence) };
  const planId = (row && row.planId) || filter.insurancePlanId;
  if (planId && planId !== 'ALL') query.planId = planId;
  return { path: BATCHES_PATH, query };
}
